import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { defer, EMPTY, from, of } from 'rxjs';
import { catchError, concatMap, exhaustMap, finalize, map, mergeMap, take, withLatestFrom } from 'rxjs/operators';
import { CACHED_REQUEST_STATUS, CachedGETRequest } from '../../classes/cached-request';
import { OfflineService } from '../../service/offline.service';
import { PaceOfflineFeatureState } from '../../store/state';
import { HttpUtilities } from '../../util/http-utilities';
import { refreshAllRequests, refreshRequest, updateGetRequestInQueue } from '../actions/refresh-request-queue.actions';
import {
    getAllRequestToBeRefreshed,
    getSpecificRequestToBeRefreshed,
} from '../selectors/refresh-request-queue.selector';
import { CACHE_FIRST_HEADER } from '../../service/service-worker-cache';

const HTTP_ERROR = 0;

@Injectable()
export class RefreshRequestQueueEffects {
    public constructor(
        private action$: Actions,
        private store: Store<PaceOfflineFeatureState>,
        private httpClient: HttpClient,
        private offlineService: OfflineService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    public refreshAllRequests = createEffect(() =>
        this.action$.pipe(
            ofType(refreshAllRequests),
            withLatestFrom(this.store.select(getAllRequestToBeRefreshed)),
            exhaustMap(([refreshParams, requests]) =>
                defer(() => {
                    this.offlineService.setSyncing(true);
                    return from(requests);
                }).pipe(
                    concatMap((cachedRequest: CachedGETRequest) => {
                        const headers = cachedRequest.headers ? { ...cachedRequest.headers } : {};
                        if (refreshParams.cacheFirst) {
                            headers[CACHE_FIRST_HEADER] = 'true';
                        }

                        this.log.info(`RefreshRequestQueueEffects: refreshAllRequests: processing:`, {
                            url: cachedRequest.url,
                            cacheFirst: refreshParams.cacheFirst,
                        });

                        return this.httpClient
                            .get(cachedRequest.url, {
                                headers,
                            })
                            .pipe(
                                map((response: HttpResponse<unknown>) =>
                                    updateGetRequestInQueue({
                                        url: cachedRequest.url,
                                        refreshStatus:
                                            response.status === HTTP_ERROR
                                                ? CACHED_REQUEST_STATUS.ERROR
                                                : CACHED_REQUEST_STATUS.COMPLETED,
                                    }),
                                ),
                                catchError((e) => {
                                    this.log.error(
                                        `RefreshRequestQueueEffects: refreshAllRequest: ${cachedRequest.url}: ${JSON.stringify(
                                            e,
                                            Object.getOwnPropertyNames(e),
                                        )}`,
                                    );

                                    // rethrowing the error here will stop the entire request queue procession
                                    return of(
                                        updateGetRequestInQueue({
                                            url: cachedRequest.url,
                                            refreshStatus: CACHED_REQUEST_STATUS.ERROR,
                                        }),
                                    );
                                }),
                            );
                    }),
                    finalize(() => {
                        this.offlineService.setSyncing(false);
                    }),
                ),
            ),
        ),
    );

    public refreshRequest = createEffect(() =>
        this.action$.pipe(
            ofType(refreshRequest),
            concatMap((param) => {
                return this.store.select(getSpecificRequestToBeRefreshed, { url: param.url }).pipe(
                    take(1),
                    mergeMap((request) => {
                        if (!request) {
                            return EMPTY;
                        }

                        this.offlineService.setSyncing(true);
                        const headers = HttpUtilities.createHttpHeader(request.headers);
                        this.log.info(`RefreshRequestQueueEffects: refreshRequest: processing: ${request.url}`);

                        return this.httpClient.get(request.url, { headers }).pipe(
                            map((response: HttpResponse<unknown>) =>
                                updateGetRequestInQueue({
                                    url: request.url,
                                    refreshStatus:
                                        response.status === HTTP_ERROR
                                            ? CACHED_REQUEST_STATUS.ERROR
                                            : CACHED_REQUEST_STATUS.COMPLETED,
                                }),
                            ),
                            catchError((e) => {
                                this.log.error(
                                    `RefreshRequestQueueEffects: refreshRequest: ${request.url}: ${JSON.stringify(
                                        e,
                                        Object.getOwnPropertyNames(e),
                                    )}`,
                                );

                                return of(
                                    updateGetRequestInQueue({
                                        url: request.url,
                                        refreshStatus: CACHED_REQUEST_STATUS.ERROR,
                                    }),
                                );
                            }),
                        );
                    }),
                    finalize(() => {
                        this.offlineService.setSyncing(false);
                    }),
                );
            }),
        ),
    );
}
